import React, { useContext, useEffect } from "react";
import { Col, DatePicker, Form, FormInstance, Input, Row, Select } from "antd";
import { TransactionProps } from "../../../interfaces/interfaces";
import dayjs from "dayjs";
import DataContext from "../../../context/DataContext";

interface Props {
  values: TransactionProps;
  setValues: React.Dispatch<React.SetStateAction<TransactionProps>>;
  form: FormInstance<any>;
}

const { Option } = Select;
const format = "MM/DD/YYYY";

export const OrderForm = ({ values, setValues, form }: Props) => {
  const {
    itemList,
    formattedItemList,
    connectionList,
    formattedConnectionList,
  } = useContext(DataContext);
  useEffect(() => {
    form.setFieldsValue(values);
  }, [form, values]);

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    event.preventDefault();
    setValues((prev) => {
      return {
        ...prev,
        [event.target.name]: event.target.value,
      };
    });
  };

  const onChangeItem = (value: string) => {
    const itemSelected = itemList.filter(
      (item) => item.OID === parseInt(value)
    );
    if (itemSelected.length > 0) {
      setValues((prev) => {
        return {
          ...prev,
          Item: itemSelected[0].OID,
          ItemDetail: itemSelected[0],
        };
      });
    }
  };

  const onChangeConnection = (value: string) => {
    const connectionSelected = connectionList.filter(
      (item) => item.OID === parseInt(value)
    );
    if (connectionSelected.length > 0) {
      setValues((prev) => {
        return {
          ...prev,
          Connection: connectionSelected[0].OID,
          ConnectionDetail: connectionSelected[0],
        };
      });
    }
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: string }
  ) =>
    ((option && option.label) || "")
      .toLowerCase()
      .includes(input.toLowerCase());
  return (
    <>
      <Form name="order" layout="vertical" initialValues={values} form={form}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Drilling Company"
              name="DrillingCompany"
              rules={[
                { required: true, message: "Please provide Drilling Company" },
              ]}
            >
              <Input
                name="DrillingCompany"
                placeholder="Drilling Company"
                value={values.DrillingCompany}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Date Received"
              rules={[
                {
                  required: true,
                  message: "Please choose the Date Received",
                },
              ]}
            >
              <DatePicker
                name="DateReceived"
                format={format}
                value={values.DateReceived && dayjs(values.DateReceived)}
                style={{ width: "100%" }}
                allowClear={false}
                onChange={(_, dateString) => {
                  setValues((prev) => {
                    return {
                      ...prev,
                      DateReceived: dayjs(dateString),
                    };
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Rig Number">
              <Input
                name="RigNumber"
                placeholder="Rig Number"
                value={values.RigNumber}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Date Repaired"
              rules={[
                {
                  required: true,
                  message: "Please choose the Date Repaired",
                },
              ]}
            >
              <DatePicker
                name="DateRepaired"
                format={format}
                value={values.DateRepaired && dayjs(values.DateRepaired)}
                style={{ width: "100%" }}
                allowClear={false}
                onChange={(_, dateString) => {
                  setValues((prev) => {
                    return {
                      ...prev,
                      DateRepaired: dayjs(dateString),
                    };
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Serial Number">
              <Input
                name="SerialNumber"
                placeholder="Serial Number"
                value={values.SerialNumber}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Date Shipped"
              rules={[
                {
                  required: true,
                  message: "Please choose the Date Shipped",
                },
              ]}
            >
              <DatePicker
                name="DateShipped"
                format={format}
                style={{ width: "100%" }}
                allowClear={false}
                value={values.DateShipped && dayjs(values.DateShipped)}
                onChange={(_, dateString) => {
                  setValues((prev) => {
                    return {
                      ...prev,
                      DateShipped: dayjs(dateString),
                    };
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="Status"
              label="Status"
              rules={[{ required: true, message: "Please choose the Status" }]}
            >
              <Select
                placeholder="Please choose the Status"
                value={values.Status}
                onChange={(newValue) => {
                  setValues((prev) => {
                    return {
                      ...prev,
                      Status: newValue,
                    };
                  });
                }}
              >
                {[
                  "",
                  "Received",
                  "Ready",
                  "Shipped",
                  "Parts",
                  "Cuts",
                  "Junked",
                ].map((item) => (
                  <Option value={item} key={item}>
                    {item}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Item"
              rules={[{ required: true, message: "Item Required" }]}
            >
              <Select
                showSearch
                placeholder="Select an item"
                optionFilterProp="children"
                value={values.ItemDetail && values.ItemDetail.OID.toString()}
                onChange={onChangeItem}
                filterOption={filterOption}
                options={formattedItemList}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Inbound Trucking Co">
              <Input
                name="InboundTruckingCo"
                placeholder="Inbound Trucking Co"
                value={values.InboundTruckingCo}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Connection"
              rules={[{ required: true, message: "Connection Required" }]}
            >
              <Select
                showSearch
                placeholder="Select Connection"
                optionFilterProp="children"
                value={
                  values.ConnectionDetail &&
                  values.ConnectionDetail.OID.toString()
                }
                onChange={onChangeConnection}
                filterOption={filterOption}
                options={formattedConnectionList}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Inbound Driver">
              <Input
                name="InboundDriver"
                placeholder="Inbound Driver"
                value={values.InboundDriver}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Estimate #">
              <Input
                name="EstimateNo"
                placeholder="Estimate #"
                value={values.EstimateNo}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Outbound Trucking Co">
              <Input
                name="OutboundTruckingCo"
                placeholder="Outbound Trucking Co"
                value={values.OutboundTruckingCo}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Purchase Order No">
              <Input
                name="PurchaseOrderNo"
                placeholder="Purchase Order No"
                value={values.PurchaseOrderNo}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Outbound Driver">
              <Input
                name="OutboundDriver"
                placeholder="Outbound Driver"
                value={values.OutboundDriver}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Notes">
              <Input.TextArea
                name="Notes"
                rows={4}
                placeholder="Notes"
                value={values.Notes}
                onChange={handleChange}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};
